import { useState, useEffect, useCallback } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card"
import { Switch } from "@/components/ui/switch"
import { Badge } from "@/components/ui/badge"
import { Loader2, Save, RefreshCw, Settings2, DollarSign, Shield, AlertTriangle, Plane, Hotel } from 'lucide-react'
import { apiFetch } from '@/lib/api'
import { toast } from 'sonner'
import { DEFAULT_CONFIG, calculatePricing, derivePricingRules } from '@/lib/subscription-rules'
import { clearConfigCache } from '@/lib/pricing-config'

interface PricingRuleRow {
  id: string
  tier: string
  product: string
  paywall_enabled: boolean
  markup_percentage: number
  transaction_fee_percentage: number
  hotel_id: string | null
  category: string | null
  description: string | null
  updated_at: string | null
}

/** Form state for a single pricing rule row */
interface RowFormState {
  id: string
  tier: string
  product: string
  markupPct: number       // displayed as whole number (e.g. 25 for 25%)
  paywallEnabled: boolean
  originalMarkupPct: number
  originalPaywallEnabled: boolean
  dirty: boolean
}

function rowLabel(tier: string, product: string): string {
  if (tier === 'default' && product === 'all') return 'Default (All Products)'
  if (tier === 'default' && product === 'flight') return 'Default Flights'
  if (tier === 'free' && product === 'hotel') return 'Free Tier Hotels'
  if (tier === 'pro' && product === 'hotel') return 'Pro Tier Hotels'
  return `${tier} / ${product}`
}

function rowDescription(tier: string, product: string): string {
  if (tier === 'default' && product === 'all') return 'Fallback rule when no product-specific or tier-specific rule matches'
  if (tier === 'default' && product === 'flight') return 'Flight pricing for all users (no tier-specific flight rules)'
  if (tier === 'free' && product === 'hotel') return 'Hotel pricing for free-tier (non-subscribed) users'
  if (tier === 'pro' && product === 'hotel') return 'Hotel pricing for subscribed (Pro/Family/Business) users'
  return ''
}

function rowIcon(product: string) {
  if (product === 'flight') return <Plane className="h-4 w-4" />
  if (product === 'hotel') return <Hotel className="h-4 w-4" />
  return <DollarSign className="h-4 w-4" />
}

function tierBadgeVariant(tier: string): "default" | "secondary" | "outline" | "destructive" {
  if (tier === 'pro') return 'default'
  if (tier === 'free') return 'secondary'
  return 'outline'
}

export default function AdminSitePage() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null) // row id being saved, or 'fee' for global fee
  const [rows, setRows] = useState<RowFormState[]>([])
  const [rawRows, setRawRows] = useState<PricingRuleRow[]>([])

  // Global transaction fee (platform-wide, propagated to all rows)
  const [transactionFeePct, setTransactionFeePct] = useState(4)
  const [originalFeePct, setOriginalFeePct] = useState(4)
  const [feeDirty, setFeeDirty] = useState(false)

  const loadConfig = useCallback(async () => {
    setLoading(true)
    try {
      const res = await apiFetch('/api/admin/pricing-rules/all')
      if (!res.ok) throw new Error('Failed to load pricing rules')
      const json = await res.json()
      if (!json.ok || !json.rules) throw new Error('Invalid response')

      const rules: PricingRuleRow[] = json.rules
      setRawRows(rules)

      // Use the first row's transaction_fee_percentage as the global fee
      // (they should all be identical after propagation)
      const fee = rules.length > 0 ? rules[0].transaction_fee_percentage : DEFAULT_CONFIG.transactionFeePercentage
      const feePctValue = Math.round(fee * 10000) / 100 // decimal to whole number, avoid float issues
      setTransactionFeePct(feePctValue)
      setOriginalFeePct(feePctValue)
      setFeeDirty(false)

      // Build form state for each row
      const formRows: RowFormState[] = rules.map(r => {
        const markupPct = Math.round(r.markup_percentage * 10000) / 100
        return {
          id: r.id,
          tier: r.tier,
          product: r.product,
          markupPct,
          paywallEnabled: r.paywall_enabled,
          originalMarkupPct: markupPct,
          originalPaywallEnabled: r.paywall_enabled,
          dirty: false,
        }
      })

      // Sort: default/all first, then default/flight, free/hotel, pro/hotel
      const order: Record<string, number> = {
        'default:all': 0,
        'default:flight': 1,
        'free:hotel': 2,
        'pro:hotel': 3,
      }
      formRows.sort((a, b) => {
        const ka = `${a.tier}:${a.product}`
        const kb = `${b.tier}:${b.product}`
        return (order[ka] ?? 99) - (order[kb] ?? 99)
      })

      setRows(formRows)
    } catch (error) {
      console.error('Failed to load config:', error)
      toast.error('Failed to load pricing rules')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadConfig()
  }, [loadConfig])

  function updateRow(id: string, updates: Partial<RowFormState>) {
    setRows(prev => prev.map(r => {
      if (r.id !== id) return r
      const updated = { ...r, ...updates }
      updated.dirty =
        updated.markupPct !== updated.originalMarkupPct ||
        updated.paywallEnabled !== updated.originalPaywallEnabled
      return updated
    }))
  }

  function updateFee(val: number) {
    const clamped = Math.max(0, Math.min(100, val))
    setTransactionFeePct(clamped)
    setFeeDirty(clamped !== originalFeePct)
  }

  async function saveGlobalFee() {
    setSaving('fee')
    try {
      // Save fee via any row — backend POST /api/admin/pricing-rules propagates
      // transaction_fee_percentage to ALL canonical rows (see adminPricingRoutes.js).
      // After loadConfig(), all rows should reflect the new fee; rawRows order doesn't matter.
      const firstRow = rawRows[0]
      if (!firstRow) throw new Error('No pricing rules found')

      const res = await apiFetch('/api/admin/pricing-rules', {
        method: 'POST',
        body: JSON.stringify({
          id: firstRow.id,
          tier: firstRow.tier,
          product: firstRow.product,
          transaction_fee_percentage: transactionFeePct / 100,
        }),
      })
      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.error || 'Save failed')
      }

      clearConfigCache()
      toast.success('Transaction fee updated across all rules')
      await loadConfig()
    } catch (error) {
      console.error('Failed to save fee:', error)
      toast.error(`Failed to save transaction fee: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setSaving(null)
    }
  }

  async function saveRow(row: RowFormState) {
    setSaving(row.id)
    try {
      const res = await apiFetch('/api/admin/pricing-rules', {
        method: 'POST',
        body: JSON.stringify({
          id: row.id,
          tier: row.tier,
          product: row.product,
          markup_percentage: row.markupPct / 100,
          paywall_enabled: row.paywallEnabled,
        }),
      })
      if (!res.ok) {
        const err = await res.json()
        throw new Error(err.error || 'Save failed')
      }

      clearConfigCache()
      toast.success(`${rowLabel(row.tier, row.product)} saved`)
      await loadConfig()
    } catch (error) {
      console.error('Failed to save row:', error)
      toast.error(`Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`)
    } finally {
      setSaving(null)
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto py-12">
        <div className="flex items-center justify-center min-h-[40vh]">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    )
  }

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
              <Settings2 className="h-8 w-8 text-primary" />
              Pricing Rules
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage markup, fees, and paywall settings for all tiers and products
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={loadConfig} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Global Transaction Fee */}
        <Card className="border-2 border-blue-200 dark:border-blue-800">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-blue-500" />
              <CardTitle>Transaction Fee (Global)</CardTitle>
            </div>
            <CardDescription>
              Applied to all transactions across all tiers and products. Changing this updates every pricing rule.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  value={transactionFeePct}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value)
                    updateFee(isNaN(val) ? 0 : val)
                  }}
                  className="w-28"
                />
                <span className="text-lg font-medium">%</span>
              </div>
              {feeDirty && (
                <Button
                  size="sm"
                  onClick={saveGlobalFee}
                  disabled={saving !== null}
                >
                  {saving === 'fee' ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-1" />
                  )}
                  Save Fee
                </Button>
              )}
              {feeDirty && (
                <span className="text-xs text-amber-600 dark:text-amber-400">Unsaved</span>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pricing Rules Table */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            Per-Rule Settings
          </h2>
          <p className="text-sm text-muted-foreground">
            Each row controls the markup and paywall for a specific tier+product combination.
          </p>

          {rows.length === 0 ? (
            <Card className="border border-dashed">
              <CardContent className="py-8 text-center text-sm text-muted-foreground">
                No pricing rules found. Check the backend connection and database.
              </CardContent>
            </Card>
          ) : rows.map((row) => (
            <Card key={row.id} className={`border ${row.dirty ? 'border-amber-300 dark:border-amber-700' : ''}`}>
              <CardContent className="pt-6">
                <div className="flex flex-col gap-4">
                  {/* Row header */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {rowIcon(row.product)}
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-semibold">{rowLabel(row.tier, row.product)}</span>
                          <Badge variant={tierBadgeVariant(row.tier)}>{row.tier}</Badge>
                          <Badge variant="outline">{row.product}</Badge>
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {rowDescription(row.tier, row.product)}
                        </p>
                      </div>
                    </div>
                    {row.dirty && (
                      <span className="text-xs text-amber-600 dark:text-amber-400 font-medium">Unsaved</span>
                    )}
                  </div>

                  {/* Row controls */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
                    {/* Markup */}
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium">Markup %</Label>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.5"
                          value={row.markupPct}
                          onChange={(e) => {
                            const val = parseFloat(e.target.value)
                            updateRow(row.id, {
                              markupPct: isNaN(val) ? 0 : Math.max(0, Math.min(100, val))
                            })
                          }}
                          className="w-24"
                        />
                        <span className="text-sm text-muted-foreground">%</span>
                      </div>
                    </div>

                    {/* Paywall */}
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium">Paywall</Label>
                      <div className="flex items-center gap-2 h-10">
                        <Switch
                          checked={row.paywallEnabled}
                          onCheckedChange={(checked) => updateRow(row.id, { paywallEnabled: checked })}
                          aria-label={`Toggle paywall for ${rowLabel(row.tier, row.product)}`}
                        />
                        <span className="text-sm text-muted-foreground">
                          {row.paywallEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                        {!row.paywallEnabled && (
                          <AlertTriangle className="h-4 w-4 text-amber-500" />
                        )}
                      </div>
                    </div>

                    {/* Save button */}
                    <div className="flex justify-end">
                      <Button
                        size="sm"
                        onClick={() => saveRow(row)}
                        disabled={!row.dirty || saving !== null}
                        variant={row.dirty ? 'default' : 'outline'}
                      >
                        {saving === row.id ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Save className="h-4 w-4 mr-1" />
                        )}
                        Save
                      </Button>
                    </div>
                  </div>

                  {/* Inline price preview */}
                  <div className="p-3 bg-muted/50 rounded-md">
                    {(() => {
                      const examplePrice = 100
                      const previewConfig = {
                        markupPercentage: row.markupPct / 100,
                        transactionFeePercentage: transactionFeePct / 100,
                        paywallEnabled: row.paywallEnabled,
                      }
                      const rules = derivePricingRules(null, previewConfig)
                      const pricing = calculatePricing(examplePrice, rules, previewConfig)
                      return (
                        <div className="flex items-center gap-6 text-sm">
                          <span className="text-muted-foreground text-xs">Preview (this row&apos;s markup on $100 base):</span>
                          <div>
                            <span className="text-muted-foreground">Standard: </span>
                            <span className="font-semibold">${pricing.standardPrice.toFixed(2)}</span>
                          </div>
                          <div>
                            <span className="text-muted-foreground">Special: </span>
                            <span className="font-semibold text-primary">${pricing.specialPrice.toFixed(2)}</span>
                          </div>
                          {pricing.savings > 0 && (
                            <div>
                              <span className="text-muted-foreground">Savings: </span>
                              <span className="font-semibold text-green-600">${pricing.savings.toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                      )
                    })()}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Reference */}
        <Card className="bg-muted/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">How Pricing Works</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p><strong>Standard Price</strong> = Base + Markup% + Transaction Fee%</p>
            <p><strong>Special Price</strong> = Base + Transaction Fee% only (no markup)</p>
            <p><strong>Transaction fee</strong> is global and applies to every transaction. <strong>Markup</strong> is per-rule and differs by tier/product.</p>
            <p><strong>Paywall</strong> controls whether subscribers see the special (lower) price for that product.</p>
            <p className="text-xs mt-3">
              Flights typically have 0% markup (standard = special). Hotels use markup to create the savings incentive for subscribers.
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
